import { autoUpdate, computePosition, offset } from '@floating-ui/dom'
import { useModals } from 'almanac-ui/Modal'
import { Edit } from 'almanac-ui/icons'
import { ReactComponent as ZoomIcon } from 'assets/icons/full16.svg'
import { CommentInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/CommentInlineFormatButton/CommentInlineFormatButton'
import { DeleteInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/DeleteInlineFormatButton'
import { NodeAlignmentButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/NodeAlignmentButton/NodeAlignmentButton'
import { TodoInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/TodoInlineFormatButton/TodoInlineFormatButton'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDivider } from 'components/InlineFormatBar/components/InlineFormatDivider/InlineFormatDivider'
import { SingleChildModal } from 'components/SingleChildModal/SingleChildModal'
import { useModalStore } from 'hooks/stores/useModalStore'
import { schema } from 'prose-core'
import {
  EditMermaidModal,
  EditMermaidModalCloseIcon,
} from 'proseShared/nodeViews/Mermaid/components/EditMermaidModal/EditMermaidModal'
import { EditorView } from 'prosemirror-view'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import store from 'redux/store'
import { DocStore } from 'stores/DocStore'
import Styles from './MermaidInlineFormatBar.module.scss'

type MermaidInlineFormatBarProps = {
  docStore: DocStore
  view: EditorView
  nodePos: number
  mermaidRef: React.RefObject<HTMLDivElement>
  editCaption: () => void
  isUnset: boolean
}

export const MermaidInlineFormatBar = ({
  docStore,
  view,
  nodePos,
  mermaidRef,
  editCaption,
  isUnset,
}: MermaidInlineFormatBarProps) => {
  const { openModal, closeModal } = useModalStore()
  const [runItemKey, setRunItemKey] = useState<string>()
  const formatBarRef = useRef<HTMLDivElement>(null)
  const mermaidNode = view.state.doc.resolve(nodePos).nodeAfter
  const mermaidSVG = mermaidRef.current?.querySelector('svg')
  const { openModal: openNewModal } = useModals()

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'm' && e.metaKey && mermaidNode) {
      e.preventDefault()
      setRunItemKey('comment')
    }
  }

  useLayoutEffect(() => {
    const refElement = mermaidRef.current
    const floatElement = formatBarRef.current
    if (!refElement || !floatElement) return
    autoUpdate(refElement, floatElement, () => {
      computePosition(refElement, floatElement, {
        placement: 'top',
        middleware: [offset({ mainAxis: 8 })],
      }).then(({ x, y }) => {
        const top = Math.round(y)
        const left = Math.round(x)
        Object.assign(floatElement.style, {
          transform: `translate(${left}px, ${top}px)`,
        })
      })
    })
  }, [mermaidRef, formatBarRef])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const queryClient = useQueryClient()
  if (!docStore || mermaidNode?.type !== schema.nodes.mermaid_embed || !docStore.layerId) return null
  const isMainLayer = docStore.layerId === docStore.mainLayerId

  const { auth, unregisteredUser } = store.getState()

  const hideTaskButton = (!auth && !unregisteredUser.user) || !isMainLayer || isUnset

  const mermaidAttrs = mermaidNode.attrs

  const updateAttributes = (updatedAttrs: Record<string, any>) => {
    const { tr } = view.state
    const newAttrs = { ...mermaidAttrs, ...updatedAttrs }
    tr.setNodeMarkup(nodePos, undefined, newAttrs)
    view.dispatch(tr)
  }

  const openEditModal = () => {
    openModal(EditMermaidModal, {
      modalProps: {
        code: mermaidAttrs.code,
        updateAttributes,
        closeModal,
      },
      modalOptions: {
        title: 'Mermaid.js Diagram',
        width: 1000,
        closeIcon: <EditMermaidModalCloseIcon />,
      },
    })
  }

  const openSingleChildModal = () => {
    if (!mermaidSVG) return

    openNewModal(
      SingleChildModal,
      {
        children: <img src={`data:image/svg+xml;base64,${window.btoa(mermaidSVG.outerHTML)}`} alt="Mermaid diagram" />,
      },
      { newStack: false },
    )
  }

  return (
    <div ref={formatBarRef} className={Styles.MermaidInlineFormatBar}>
      <InlineFormatBar>
        <InlineFormatButton label="Edit" data-test-id="mermaid-edit-button" onClick={openEditModal}>
          <Edit />
          Edit
        </InlineFormatButton>
        <InlineFormatDivider />
        {!isUnset && (
          <>
            <NodeAlignmentButton
              view={view}
              nodePos={nodePos}
              alignment={mermaidAttrs.float}
              nodeAttrs={mermaidAttrs}
            />
            <InlineFormatDivider />
          </>
        )}
        {!isUnset && (
          <>
            <InlineFormatButton label="Caption" data-test-id="mermaid-caption-button" onClick={editCaption}>
              Caption
            </InlineFormatButton>
            <InlineFormatDivider />
          </>
        )}
        {mermaidSVG && mermaidAttrs.code && (
          <InlineFormatButton label="Zoom" data-test-id="mermaid-zoom-button" onPress={openSingleChildModal}>
            <ZoomIcon />
          </InlineFormatButton>
        )}
        <CommentInlineFormatButton
          hide={isUnset}
          view={view}
          runItemKey={runItemKey}
          layerId={docStore.layerId}
          queryClient={queryClient}
          docStore={docStore}
          nodePos={nodePos}
        />
        <TodoInlineFormatButton
          view={view}
          nodePos={nodePos}
          queryClient={queryClient}
          docStore={docStore}
          hide={hideTaskButton}
        />
        <DeleteInlineFormatButton view={view} />
      </InlineFormatBar>
    </div>
  )
}
